import { useMemo } from 'react';
import { Box, Checkbox, CircularProgress, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { useLanguage } from '../../../../context/LanguageContext';
import { useExportFields } from '../../../../hooks/QueryBuilder';

const EXPORT_KEY = 'pages.clients.queryBuilder.exportDialog';

const normalizeForMatch = (value: string): string =>
    value.trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

const REQUIRED_FIELD_LABELS = new Set([
    'civilite',
    'nom',
    'nom du client',
    'prenom',
    'prenom du client',
    'numero de carte de fidelite',
]);

interface ExportFieldsSelectorProps {
    selected: string[];
    disabled: boolean;
    onToggle: (code: string) => void;
}

function ExportFieldsSelector({ selected, disabled, onToggle }: ExportFieldsSelectorProps) {
    const { t } = useLanguage();
    const { fields, loading, error } = useExportFields();
    const optionalFields = useMemo(
        () => fields.filter(field => !REQUIRED_FIELD_LABELS.has(normalizeForMatch(field.label))),
        [fields]
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">{t(`${EXPORT_KEY}.fieldsLoading`)}</Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography variant="body2" color="error">{t(`${EXPORT_KEY}.fieldsError`)}</Typography>;
    }

    if (optionalFields.length === 0) {
        return <Typography variant="body2" color="text.secondary">{t(`${EXPORT_KEY}.noOptionalFields`)}</Typography>;
    }

    return (
        <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, columnGap: 1 }}>
            {optionalFields.map(field => (
                <FormControlLabel
                    key={field.code}
                    control={(
                        <Checkbox
                            size="small"
                            checked={selected.includes(field.code)}
                            onChange={() => onToggle(field.code)}
                            disabled={disabled}
                        />
                    )}
                    label={field.label}
                />
            ))}
        </FormGroup>
    );
}

export default ExportFieldsSelector;
